// New releases while the app is open: compares the hashed module script this page started from with the one the site's index.html names now (fetched uncached when the tab becomes visible, every 5 minutes while it is, and soon after a response that broke the API contract); once they differ, a lasting toast offers a reload and the next in-app navigation becomes a full page load. A script chunk that fails to load (vite:preloadError) reloads the page, at most once in 30 s. Pages without a hashed entry script (the dev server, tests) are never checked.
import type { RouterHistory } from "@tanstack/react-router";

import { entryScript } from "@/app/entry-script";
import { notify } from "@/components/ui/sonner";

const CHECK_INTERVAL_MS = 5 * 60_000;
const MIN_CHECK_GAP_MS = 60_000;
const RELOAD_GUARD_KEY = "gridcode:release-reload";
const RELOAD_GUARD_MS = 30_000;

export const CONTRACT_ERROR_GAP_MS = 5_000;
export const RELEASE_TOAST_ID = "gridcode-release";

export interface ReleaseWatchOptions {
  history: Pick<RouterHistory, "block">;
  fetchPage?: () => Promise<Response>;
  now?: () => number;
  reload?: () => void;
  open?: (href: string, replace: boolean) => void;
}

export interface ReleaseWatch {
  check: (minGapMs?: number) => Promise<boolean>;
  stop: () => void;
}

export function servedEntry(html: string): string | null {
  return entryScript(new DOMParser().parseFromString(html, "text/html"));
}

function fetchSitePage(): Promise<Response> {
  return fetch(new URL("/", window.location.origin), { cache: "no-store", headers: { Accept: "text/html" } });
}

function openPage(href: string, replace: boolean): void {
  if (replace) {
    window.location.replace(href);
  } else {
    window.location.assign(href);
  }
}

export function watchReleases({
  history,
  fetchPage = fetchSitePage,
  now = Date.now,
  reload = () => window.location.reload(),
  open = openPage,
}: ReleaseWatchOptions): ReleaseWatch {
  const running = entryScript(document);
  if (running === null) {
    return { check: () => Promise.resolve(false), stop: () => undefined };
  }
  let outdated = false;
  let lastCheck = Number.NEGATIVE_INFINITY;
  let pending: Promise<boolean> | null = null;
  let unblock: () => void = () => undefined;

  async function fetchEntry(): Promise<string | null> {
    try {
      const response = await fetchPage();
      if (!response.ok || !(response.headers.get("content-type") ?? "").includes("text/html")) {
        return null;
      }
      return servedEntry(await response.text());
    } catch {
      return null;
    }
  }

  function markOutdated(): void {
    outdated = true;
    notify("GridCode has been updated", "Reload to use the new version.", {
      id: RELEASE_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      action: { label: "Reload", onClick: reload },
    });
    unblock = history.block({
      blockerFn: ({ nextLocation, action }) => {
        open(nextLocation.href, action === "REPLACE");
        return true;
      },
      enableBeforeUnload: false,
    });
  }

  function check(minGapMs = MIN_CHECK_GAP_MS): Promise<boolean> {
    if (outdated) {
      return Promise.resolve(true);
    }
    if (pending !== null) {
      return pending;
    }
    const at = now();
    if (at - lastCheck < minGapMs) {
      return Promise.resolve(false);
    }
    lastCheck = at;
    pending = fetchEntry().then((served) => {
      pending = null;
      if (served !== null && served !== running && !outdated) {
        markOutdated();
      }
      return outdated;
    });
    return pending;
  }

  function reloadOnce(): boolean {
    const at = now();
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? Number.NEGATIVE_INFINITY);
      if (at - last < RELOAD_GUARD_MS) {
        return false;
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(at));
    } catch {
      return false;
    }
    reload();
    return true;
  }

  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      void check();
    }
  };
  const onPreloadError = (event: Event) => {
    if (reloadOnce()) {
      event.preventDefault();
    }
  };
  const timer = window.setInterval(onVisibility, CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("vite:preloadError", onPreloadError);

  return {
    check,
    stop: () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("vite:preloadError", onPreloadError);
      unblock();
    },
  };
}
